import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { API_BASE, authHeaders, handleResponse } from "../api/api";

/**
 * Separate from AuthContext (buyer/admin User) on purpose — a Seller is a
 * different principal shape entirely, and a person can plausibly be logged
 * in as a buyer and a seller at once in the same browser. Storage key
 * (hr_seller_token) is deliberately distinct from AuthContext's hr_token.
 *
 * v1: a single 12-hour session token, no silent refresh (see
 * seller_login_verify_otp in main.py) — re-login via OTP once a day.
 */
const SellerAuthContext = createContext(null);

export const SellerAuthProvider = ({ children }) => {
  const [seller, setSeller] = useState(() => {
    const stored = localStorage.getItem("hr_seller");
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(localStorage.getItem("hr_seller_token"));

  // channel: "phone" (text, the default) or "email" — the code then goes to
  // the email address already on the seller's account (never one typed
  // here). Resolves to { channel, sent_to }, sent_to masked by the server.
  const requestOtp = useCallback(async (phone, channel = "phone") => {
    const response = await fetch(`${API_BASE}/seller/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, channel }),
    });
    return handleResponse(response);
  }, []);

  const verifyOtp = useCallback(async (phone, code) => {
    const response = await fetch(`${API_BASE}/seller/login/verify-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, code }),
    });
    const data = await handleResponse(response);
    const sellerInfo = { id: data.seller_id, name: data.name };
    localStorage.setItem("hr_seller_token", data.access_token);
    localStorage.setItem("hr_seller", JSON.stringify(sellerInfo));
    setToken(data.access_token);
    setSeller(sellerInfo);
    return sellerInfo;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("hr_seller_token");
    localStorage.removeItem("hr_seller");
    setToken(null);
    setSeller(null);
  }, []);

  const sellerFetch = useCallback(
    async (path, options = {}) => {
      const response = await fetch(`${API_BASE}${path}`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(token),
          ...(options.headers || {}),
        },
        method: options.method || "GET",
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
      return handleResponse(response);
    },
    [token]
  );

  const value = useMemo(
    () => ({ seller, token, requestOtp, verifyOtp, logout, sellerFetch }),
    [seller, token, requestOtp, verifyOtp, logout, sellerFetch]
  );

  return <SellerAuthContext.Provider value={value}>{children}</SellerAuthContext.Provider>;
};

export const useSellerAuth = () => useContext(SellerAuthContext);
